import React, { useState } from 'react';
import { Box, Typography, Tooltip, Badge, Collapse, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { TOURNAMENT_RANKS } from './RankFilter';

// Priorytet rang turniejów do sortowania
const RANK_PRIORITY = {
  'INTERNATIONAL': 1,
  'NATIONAL': 2,
  'FOREIGN': 3,
  'REGIONAL': 4,
  'LOCAL': 5,
  undefined: 6 // dla wydarzeń bez określonej rangi
};

const CalendarDay = ({ day, events, currentMonth, onEventClick }) => {
  const [expanded, setExpanded] = useState(false);

  if (!day) return <Box sx={{
    padding: '0.5rem',
    minHeight: '8rem',
    backgroundColor: 'transparent'
  }} />;

  // Filtrowanie wydarzeń dla bieżącego dnia
  const dayEvents = events.filter(event => {
    const eventDate = new Date(event.date);
    return eventDate.getDate() === day &&
      eventDate.getMonth() === currentMonth.getMonth() &&
      eventDate.getFullYear() === currentMonth.getFullYear();
  });

  // Sortowanie wydarzeń według rangi w określonej kolejności
  const sortedEvents = [...dayEvents].sort((a, b) => {
    const rankA = RANK_PRIORITY[a.rank] || RANK_PRIORITY.undefined;
    const rankB = RANK_PRIORITY[b.rank] || RANK_PRIORITY.undefined;
    return rankA - rankB;
  });

  // Stałe do wyświetlania
  const MAX_VISIBLE_EVENTS = 3;
  const hasMoreEvents = sortedEvents.length > MAX_VISIBLE_EVENTS;
  const visibleEvents = expanded ? sortedEvents : sortedEvents.slice(0, MAX_VISIBLE_EVENTS);
  const hiddenCount = sortedEvents.length - MAX_VISIBLE_EVENTS;

  // Sprawdź, czy dzień jest dzisiejszy
  const today = new Date();
  const isToday = day === today.getDate() &&
    currentMonth.getMonth() === today.getMonth() &&
    currentMonth.getFullYear() === today.getFullYear();

  return (
    <Box sx={{
      padding: '0.5rem',
      border: '1px solid rgb(55, 65, 81)',
      borderRadius: '0.5rem',
      minHeight: '8rem',
      backgroundColor: isToday ? 'rgba(76, 29, 149, 0.1)' : 'transparent',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      transition: 'all 0.2s',
      '&:hover': {
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      }
    }}>
      {/* Numer dnia z licznikiem wydarzeń */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 0.5
      }}>
        <Typography sx={{
          fontSize: '0.875rem',
          fontWeight: '400',
          color: 'inherit',
        }}>
          {day}
        </Typography>
      </Box>

      {/* Lista wydarzeń */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {visibleEvents.map((event, index) => (
          <Box
            sx={{
              backgroundColor: event.rank ? `${TOURNAMENT_RANKS[event.rank].color}40` : 'rgba(76, 29, 149, 0.2)',
              borderLeft: event.rank ? `3px solid ${TOURNAMENT_RANKS[event.rank].color}` : 'none',
              borderRadius: '0.25rem',
              padding: '0.25rem',
              marginBottom: '0.25rem',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: event.rank ? `${TOURNAMENT_RANKS[event.rank].color}60` : 'rgba(76, 29, 149, 0.4)',
              },
              transition: 'background-color 0.2s',
              overflow: 'hidden'
            }}
            onClick={() => onEventClick(event)}
          >
            <Typography
              noWrap
              sx={{
                fontSize: '0.75rem',
                color: 'white',
                fontWeight: '500',
                lineHeight: '1.2'
              }}
            >
              {event.isMultiDayCopy ? `${event.name} (Dzień ${event.dayNumber})` : event.name}
            </Typography>
            <Typography
              noWrap
              sx={{
                fontSize: '0.65rem',
                color: 'rgb(196, 181, 253)',
                lineHeight: '1.2',
                fontStyle: 'italic'
              }}
            >
              {event.city} - {event.customLocation ? event.customLocation.name : event.organizer?.name || 'Nieznany organizator'}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Przycisk rozwijania/zwijania */}
      {hasMoreEvents && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 'auto',
            pt: 0.5,
            cursor: 'pointer',
            borderTop: '1px dashed rgba(196, 181, 253, 0.3)',
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Typography
            sx={{
              fontSize: '0.7rem',
              color: 'rgb(196, 181, 253)',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {expanded ? (
              <>
                <ExpandLessIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                Zwiń
              </>
            ) : (
              <>
                <ExpandMoreIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                Pokaż więcej ({hiddenCount})
              </>
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CalendarDay;